import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que Recopilamos',
    text: 'Recopilamos únicamente la información necesaria para procesar tu compra: nombre, dirección de correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito en nuestros servidores.'
  }, {
    heading: 'Uso de la Información',
    text: 'Tu información personal se utiliza exclusivamente para: entregar el producto digital adquirido, enviar confirmaciones de compra y comunicarnos contigo en caso de ser necesario respecto a tu pedido.'
  }, {
    heading: 'Protección de Datos',
    text: 'Implementamos medidas de seguridad estándar de la industria para proteger tu información personal. Utilizamos cifrado SSL para todas las transacciones y no compartimos tus datos con terceros sin tu consentimiento.'
  }, {
    heading: 'Cookies',
    text: 'Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación y analizar el tráfico del sitio. Puedes configurar tu navegador para rechazar cookies si lo prefieres.'
  }, {
    heading: 'Tus Derechos',
    text: 'Tienes derecho a acceder, corregir o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes comunicarte con nosotros a través de los canales disponibles.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Producto Digital',
    text: 'El Plan Lonchera Cero Estrés es un producto digital descargable en formato PDF. Una vez completada la compra, recibirás acceso inmediato al material. Este es un pago único que incluye acceso de por vida al contenido adquirido.'
  }, {
    heading: 'Uso Personal',
    text: 'El contenido adquirido es para uso personal y familiar únicamente. Queda prohibida la reproducción, distribución, reventa o compartición del material con terceros sin autorización expresa.'
  }, {
    heading: 'Propiedad Intelectual',
    text: 'Todo el contenido, incluyendo textos, diseños, imágenes y plantillas, está protegido por derechos de autor. La compra otorga una licencia de uso personal, no la transferencia de propiedad intelectual.'
  }, {
    heading: 'Política de Reembolso',
    text: 'Debido a la naturaleza digital del producto y su entrega inmediata, todas las ventas son finales. Si experimentas problemas técnicos con la descarga, te asistiremos para resolver cualquier inconveniente.'
  }, {
    heading: 'Limitación de Responsabilidad',
    text: 'El contenido proporcionado es de carácter informativo y orientativo. Los resultados pueden variar según las circunstancias individuales de cada familia. No nos hacemos responsables por alergias alimentarias no declaradas o necesidades dietéticas específicas no contempladas.'
  }, {
    heading: 'Modificaciones',
    text: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones no afectarán las compras ya realizadas.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="bg-brand-brown py-8" data-id="element-33">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-34">
          <p className="text-white/40 text-sm" data-id="element-35">
            © {new Date().getFullYear()} El Plan Lonchera Cero Estrés. Todos
            los derechos reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-36">
            <button onClick={() => setActiveModal('privacy')} className="text-white/60 hover:text-white text-sm transition-colors duration-200 underline-offset-4 hover:underline" data-id="element-37">
              Política de Privacidad
            </button>
            <span className="text-white/20" data-id="element-38">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-white/60 hover:text-white text-sm transition-colors duration-200 underline-offset-4 hover:underline" data-id="element-39">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-40">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-41">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-42" />

            {/* Modal content */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden z-10" data-id="element-43">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-brand-brown/10 px-6 py-5 flex items-center justify-between z-10" data-id="element-44">
                <h2 className="text-xl font-bold text-brand-brown" data-id="element-45">
                  {content.title}
                </h2>
                <button onClick={() => setActiveModal(null)} className="text-brand-brown/40 hover:text-brand-brown transition-colors p-1 rounded-full hover:bg-brand-brown/5" aria-label="Cerrar" data-id="element-46">
                  <XIcon className="w-5 h-5" data-id="element-47" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-72px)]" data-id="element-48">
                <div className="space-y-6" data-id="element-49">
                  {content.sections.map((section, idx) => <div key={idx} data-id="element-50">
                      <h3 className="text-base font-bold text-brand-brown mb-2" data-id="element-51">
                        {section.heading}
                      </h3>
                      <p className="text-brand-brown/70 text-sm leading-relaxed" data-id="element-52">
                        {section.text}
                      </p>
                    </div>)}
                </div>

                <p className="text-xs text-brand-brown/40 mt-8 pt-4 border-t border-brand-brown/10" data-id="element-53">
                  Última actualización: Marzo 2026
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}